import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogHeader,
    DialogBody,
    Button,
} from "@material-tailwind/react";
import {
    XMarkIcon,
    DocumentTextIcon,
    EyeIcon,
    ArrowDownTrayIcon,
} from "@heroicons/react/24/solid";
import { useDispatch, useSelector } from "react-redux";
import { getUploadedPickupProof } from "@/store/slices/newDeliverySlice";
import { useTranslation } from "react-i18next";

function nameFromUrl(url) {
    try {
        const u = new URL(url);
        const last = u.pathname.split("/").pop();
        return decodeURIComponent(last || "Receipt");
    } catch {
        const last = String(url || "").split("/").pop();
        return last || "Receipt";
    }
}

export default function ViewUploadedPickupProofModal({
    open,
    onClose,
    deliveryAssignedOrderId,
}) {
    const dispatch = useDispatch();
    const { t } = useTranslation('order');
    const { uploaded_pickup_proofes } = useSelector((state) => state.delivery);

    const [isBootLoading, setIsBootLoading] = useState(false);

    useEffect(() => {
        if (!open || !deliveryAssignedOrderId) return;

        let cancelled = false;

        setIsBootLoading(true);

        dispatch(getUploadedPickupProof({ id: deliveryAssignedOrderId }))
            .unwrap()
            .catch(() => { })
            .finally(() => {
                if (!cancelled) setIsBootLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, deliveryAssignedOrderId, dispatch]);

    const list = Array.isArray(uploaded_pickup_proofes) ? uploaded_pickup_proofes : [];

    const { images, receipts } = useMemo(() => {
        const imgs = list
            .filter((p) => p?.type === "image" && p?.file)
            .map((p) => ({
                id: p.id,
                file: p.file,
                timestamp: p?.created_at || p?.updated_at || "",
            }));

        const recs = list
            .filter((p) => p?.type === "receipt" && p?.file)
            .map((p) => ({
                id: p.id,
                file: p.file,
                name: nameFromUrl(p.file),
            }));

        return { images: imgs, receipts: recs };
    }, [list]);

    const firstRemark = useMemo(
        () => list.find((p) => p?.remark)?.remark || "",
        [list]
    );

    const handleDownload = async (url, filename) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = filename || "download";

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Download failed:", error);
            window.open(url, "_blank");
        }
    };

    return (
        <Dialog
            size="lg"
            open={open}
            handler={() => onClose?.()}
            className="rounded-2xl"
        >
            <DialogHeader className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-base font-bold text-gray-900 truncate">
                        {t('uploaded_pickup_proof_title')}
                    </div>

                    <div className="text-xs text-gray-500">
                        {t('delivery_assigned_order_id_label')}:{" "}
                        <span className="font-semibold text-gray-700">
                            {deliveryAssignedOrderId || "—"}
                        </span>
                    </div>

                    {firstRemark && (
                        <div className="text-xs text-gray-500 mt-1">
                            {t('remark_label')}:{" "}
                            <span className="font-medium text-gray-700">
                                {firstRemark}
                            </span>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    <Button
                        variant="text"
                        color="blue-gray"
                        onClick={() => onClose?.()}
                        className="p-2"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </Button>
                </div>
            </DialogHeader>

            <DialogBody className="pt-2 overflow-y-auto max-h-[70vh]">
                {isBootLoading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <div className="text-sm text-gray-500 animate-pulse">
                            {t('loading_uploaded_proofs')}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-6 pr-2">
                        {/* Images */}
                        <div>
                            <div className="flex items-center justify-between gap-3 mb-4 sticky top-0 bg-white z-10 py-1">
                                <h3 className="text-sm sm:text-base font-semibold text-gray-800">
                                    {t('pickup_proof_images_title')}
                                </h3>

                                <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full shrink-0">
                                    {t('images_uploaded_count', { count: images.length })}
                                </span>
                            </div>

                            {images.length === 0 ? (
                                <div className="text-sm text-gray-500 bg-gray-50 border border-dashed border-gray-200 rounded-xl p-4">
                                    {t('no_images_uploaded')}
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
                                    {images.map((img) => (
                                        <button
                                            type="button"
                                            key={img.id}
                                            onClick={() =>
                                                window.open(img.file, "_blank", "noopener,noreferrer")
                                            }
                                            className="relative bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden text-left hover:shadow-md transition"
                                        >
                                            <img
                                                src={img.file}
                                                alt="uploaded"
                                                className="h-40 w-full object-cover"
                                                loading="lazy"
                                            />

                                            <div className="text-[10px] text-gray-500 p-2 font-medium truncate">
                                                {img.timestamp || "—"}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Receipts */}
                        <div>
                            <div className="flex items-center justify-between gap-3 mb-4 sticky top-0 bg-white z-10 py-1">
                                <h3 className="text-sm sm:text-base font-semibold text-gray-800">
                                    {t('pickup_receipts_title')}
                                </h3>

                                <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full shrink-0">
                                    {t('receipts_uploaded_count', { count: receipts.length })}
                                </span>
                            </div>

                            {receipts.length === 0 ? (
                                <div className="text-sm text-gray-500 bg-gray-50 border border-dashed border-gray-200 rounded-xl p-4">
                                    {t('no_receipts_uploaded')}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {receipts.map((receipt) => (
                                        <div
                                            key={receipt.id}
                                            className="relative overflow-hidden flex items-center justify-between gap-3 bg-[#FFF1F1] p-3 rounded-lg border border-[#FFDEDE]"
                                        >
                                            <div className="flex items-center gap-2 min-w-0">
                                                <DocumentTextIcon className="h-5 w-5 text-gray-700 shrink-0" />

                                                <div className="min-w-0">
                                                    <div className="text-xs font-semibold text-gray-800 truncate">
                                                        {receipt.name}
                                                    </div>

                                                    <div className="text-[10px] text-gray-500 truncate">
                                                        {receipt.file}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-1 shrink-0">
                                                <button
                                                    type="button"
                                                    className="p-2 rounded-md hover:bg-white/70 transition"
                                                    onClick={() =>
                                                        window.open(receipt.file, "_blank", "noopener,noreferrer")
                                                    }
                                                >
                                                    <EyeIcon className="h-5 w-5 text-[#F4B11F]" />
                                                </button>

                                                <button
                                                    type="button"
                                                    className="p-2 rounded-md hover:bg-white/70 transition"
                                                    onClick={() =>
                                                        handleDownload(receipt.file, receipt.name)
                                                    }
                                                >
                                                    <ArrowDownTrayIcon className="h-5 w-5 text-blue-600" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </DialogBody>
        </Dialog>
    );
}